import React, { useEffect, useState } from 'react';
import { X, MoreHorizontal, Compass, ArrowUpRight } from 'lucide-react';
import {
  isInAppBrowser,
  isIOS,
  isMailWebview,
} from '../utils/inAppBrowser';
import { isStandalonePWA } from '../utils/iosInstallContext';

const FONT = "'Inria Serif', serif";
// Session-scoped dismiss key. We re-nudge on the next full page load
// because the whole point is that the user can't install / deep-link
// from inside this webview.
const DISMISS_KEY = 'allure_in_app_browser_nudge_dismissed';

/**
 * InAppBrowserBanner — full-screen dismissible sheet shown to iPhone
 * users when we detect they're browsing Allure inside a third-party
 * in-app WebView (Gmail, Outlook, Instagram, Facebook, TikTok,
 * LinkedIn, WhatsApp, etc.).
 *
 * WHY: These WebViews block:
 *   • iOS custom URI schemes and Universal Links (breaking Viva
 *     Wallet Apple Pay return, Google Login handoff, deep-link push
 *     taps).
 *   • Apple's "Add to Home Screen" flow — which is the only way an
 *     iPhone user can install the PWA and keep the app.
 *
 * So users who tap a link inside a marketing email or Instagram bio
 * can create an account and then effectively lose the app the
 * moment they close that webview. This full-screen sheet is a
 * one-tap escape hatch.
 *
 * Android is intentionally untouched (Android WebViews handle
 * intents differently — Chrome Custom Tabs / intent filters just
 * work).
 *
 * Behaviour:
 *   • Only renders on iOS + inside a known in-app browser.
 *   • Never renders once the PWA is installed (standalone mode).
 *   • Dismissible via a small "Continue anyway" link — the dismiss
 *     is session-scoped (sessionStorage) so it comes back next
 *     time the link is opened.
 */
export const InAppBrowserBanner = () => {
  const [visible, setVisible] = useState(false);
  const [inMail, setInMail] = useState(false);

  useEffect(() => {
    if (!isIOS() || !isInAppBrowser() || isStandalonePWA()) return;
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === '1') return;
    } catch (_e) {
      /* sessionStorage blocked — still show */
    }
    setInMail(isMailWebview());
    setVisible(true);
  }, []);

  const handleDismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch (_e) { /* noop */ }
    setVisible(false);
  };

  if (!visible) return null;

  const title = inMail
    ? 'Open Allure in Safari'
    : 'Continue in Safari';
  const subtitle = inMail
    ? 'Links opened inside Mail can’t install Allure on your Home Screen. Reopen in Safari so you keep the app after signing up.'
    : 'This in-app browser blocks sign-in and payments. Reopen in Safari for the full experience.';

  return (
    <div
      data-testid="in-app-browser-nudge"
      role="dialog"
      aria-modal="true"
      aria-labelledby="in-app-browser-nudge-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9998,
        background: 'rgba(11, 6, 24, 0.72)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        display: 'flex',
        alignItems: 'flex-end',
        fontFamily: FONT,
      }}
    >
      <div
        style={{
          width: '100%',
          background: 'linear-gradient(160deg, #1A1330 0%, #2A1758 55%, #4B22B8 100%)',
          color: '#FFFFFF',
          borderTopLeftRadius: 28,
          borderTopRightRadius: 28,
          padding: 'calc(28px + env(safe-area-inset-top, 0px)) 22px calc(24px + env(safe-area-inset-bottom, 0px))',
          boxShadow: '0 -14px 44px rgba(0, 0, 0, 0.45)',
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
        }}
      >
        {/* Drag pill for affordance */}
        <div
          style={{
            width: 44,
            height: 4,
            borderRadius: 999,
            background: 'rgba(255,255,255,0.28)',
            margin: '0 auto',
          }}
        />

        {/* Icon */}
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 18,
            background: 'rgba(255,255,255,0.12)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            alignSelf: 'flex-start',
          }}
        >
          <Compass size={28} strokeWidth={1.8} color="#FFFFFF" />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <h2
            id="in-app-browser-nudge-title"
            style={{
              margin: 0,
              fontFamily: FONT,
              fontSize: 26,
              lineHeight: 1.15,
              fontWeight: 400,
              letterSpacing: '-0.01em',
            }}
          >
            {title}
          </h2>
          <p
            style={{
              margin: 0,
              fontSize: 15,
              lineHeight: 1.5,
              color: 'rgba(255,255,255,0.82)',
              fontFamily: 'Manrope, sans-serif',
            }}
          >
            {subtitle}
          </p>
        </div>

        {/* Step list */}
        <ol
          style={{
            margin: 0,
            padding: 0,
            listStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            fontFamily: 'Manrope, sans-serif',
          }}
        >
          <li style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span
              style={{
                width: 28,
                height: 28,
                borderRadius: 999,
                background: 'rgba(255,255,255,0.14)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 13,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              1
            </span>
            <span style={{ fontSize: 14.5, lineHeight: 1.4, color: 'rgba(255,255,255,0.92)' }}>
              Tap the{' '}
              <MoreHorizontal
                size={16}
                strokeWidth={2.4}
                style={{ display: 'inline', verticalAlign: '-3px', margin: '0 3px' }}
              />{' '}
              menu (top-right)
            </span>
          </li>
          <li style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span
              style={{
                width: 28,
                height: 28,
                borderRadius: 999,
                background: 'rgba(255,255,255,0.14)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 13,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              2
            </span>
            <span style={{ fontSize: 14.5, lineHeight: 1.4, color: 'rgba(255,255,255,0.92)' }}>
              Pick <strong>“Open in Safari”</strong> or{' '}
              <strong>“Open in External Browser”</strong>
            </span>
          </li>
          <li style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span
              style={{
                width: 28,
                height: 28,
                borderRadius: 999,
                background: 'rgba(255,255,255,0.14)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 13,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              3
            </span>
            <span style={{ fontSize: 14.5, lineHeight: 1.4, color: 'rgba(255,255,255,0.92)' }}>
              Once Safari opens, you can Add Allure to your Home Screen
            </span>
          </li>
        </ol>

        {/* Hint chip */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            alignSelf: 'flex-start',
            gap: 8,
            background: 'rgba(255,255,255,0.10)',
            border: '1px solid rgba(255,255,255,0.14)',
            padding: '8px 12px',
            borderRadius: 999,
            fontFamily: 'Manrope, sans-serif',
            fontSize: 12.5,
            color: 'rgba(255,255,255,0.85)',
          }}
        >
          <ArrowUpRight size={14} strokeWidth={2.2} />
          Safari is the only place Allure can be installed on iPhone
        </div>

        {/* Dismiss link */}
        <button
          data-testid="in-app-browser-nudge-dismiss"
          onClick={handleDismiss}
          style={{
            marginTop: 4,
            background: 'transparent',
            border: 'none',
            color: 'rgba(255,255,255,0.62)',
            fontFamily: 'Manrope, sans-serif',
            fontSize: 13.5,
            textDecoration: 'underline',
            textUnderlineOffset: 3,
            padding: '8px 4px',
            cursor: 'pointer',
            alignSelf: 'center',
          }}
        >
          Continue anyway
        </button>
      </div>

      {/* Corner close (accessibility) */}
      <button
        data-testid="in-app-browser-nudge-close"
        onClick={handleDismiss}
        aria-label="Close"
        style={{
          position: 'absolute',
          top: 'calc(env(safe-area-inset-top, 0px) + 14px)',
          right: 14,
          background: 'rgba(0,0,0,0.35)',
          border: '1px solid rgba(255,255,255,0.16)',
          borderRadius: 999,
          width: 34,
          height: 34,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#FFFFFF',
          cursor: 'pointer',
        }}
      >
        <X size={16} strokeWidth={2.2} />
      </button>
    </div>
  );
};

export default InAppBrowserBanner;
